import { InstalledSensorStatus, ExistingImageDto, SignalQuality, BatteryStatus } from '@/api_generated/api'
import { z } from 'zod'

const InstalledSensorStatusSchema = z.enum([
    InstalledSensorStatus.Start,
    InstalledSensorStatus.Onboarded,
    InstalledSensorStatus.Unsuccessful,
    InstalledSensorStatus.Draft,
    InstalledSensorStatus.Deleted,
    InstalledSensorStatus.Prepared,
    InstalledSensorStatus.Active,
    InstalledSensorStatus.Offline,
])

const ExistingImageSchema: z.ZodType<ExistingImageDto> = z.object({
    url: z.string(),
    description: z.string().nullish().optional(),
})

const TimeseriesValueDtoSchema = z.object({
    value: z.number().optional(),
    time: z.string().optional(),
})

const LastRequestedOperationSchema = z.union([z.literal('Onboard'), z.literal('Rollback'), z.literal('Delete')])
export const SensorSchema = z.object({
    sensorDbKey: z.string().nullish().optional(),
    plant: z.string().nullish().optional(),
    plantId: z.string().nullish().optional(),
    area: z.string().nullish().optional(),
    superiorTag: z.string().nullish().optional(),
    project: z.string().nullish().optional(),
    coordinates: z.string().nullish().optional(),
    tag: z.string().nullish().optional(),
    vendorSerialNumber: z.string().nullish().optional(),
    sensorModel: z.string().nullish().optional(),
    sensorType: z.string().nullish().optional(),
    protocol: z.string().nullish().optional(),
    imageFiles: z.array(z.instanceof(File)).optional(),
    imageDescriptions: z.array(z.string()).optional(),
    userAddedDescription: z.string().nullish().optional(),
    statusMessage: z.string().nullish().optional(),
    lastRequestedOperation: LastRequestedOperationSchema.nullish().optional(),
    status: InstalledSensorStatusSchema.nullish().optional(),
    coordinatesNotAvailable: z.boolean(),
    draft: z.boolean(),
    installationDate: z
        .string()
        .nullish()
        .optional()
        .transform((dateString) => {
            if (dateString) {
                return new Date(dateString)
            }
            return null
        }),
    firstActive: z
        .string()
        .nullish()
        .optional()
        .transform((dateString) => {
            if (dateString) {
                return new Date(dateString)
            }
            return null
        }),
    existingImages: z.array(ExistingImageSchema).nullish().optional(),
    hasAccess: z.boolean(),
    battery: TimeseriesValueDtoSchema.nullish().optional(),
    batteryStatus: z.nativeEnum(BatteryStatus).nullish().optional(),
    batteryEstimated: z.boolean().nullish().optional(),
    rssi: TimeseriesValueDtoSchema.nullish().optional(),
    snr: TimeseriesValueDtoSchema.nullish().optional(),
    sf: TimeseriesValueDtoSchema.nullish().optional(),
    signalQuality: z.nativeEnum(SignalQuality).nullish().optional(),
    lastReceived: z.string().nullish().optional(),
})

export const FormDataSchema = z.object({
    plant: z.string().nullish(),
    project: z.string(),
    sensorModel: z.string(),
    sensors: z.array(SensorSchema),
})

export type FormData = z.infer<typeof FormDataSchema>

export type LastRequestedOperation = z.infer<typeof LastRequestedOperationSchema>

export type Sensor = z.infer<typeof SensorSchema>

const EDITABLE_SENSOR_STATUSES: InstalledSensorStatus[] = [
    InstalledSensorStatus.Draft,
    InstalledSensorStatus.Unsuccessful,
    InstalledSensorStatus.Active,
    InstalledSensorStatus.Prepared,
    InstalledSensorStatus.Offline,
]

export const isSensorStatusEditable = (status?: InstalledSensorStatus | null): boolean =>
    !!status && EDITABLE_SENSOR_STATUSES.includes(status)

/** Single source of truth for whether a user may edit, configure or delete a sensor. */
export const isSensorEditable = (sensor?: Sensor | null): boolean =>
    !!sensor && !!sensor.hasAccess && isSensorStatusEditable(sensor.status)

/** Returns an explanation for why a sensor cannot be modified, or an empty string when it can. */
export const getSensorEditDisabledReason = (sensor?: Sensor | null): string => {
    if (!sensor) return 'No sensor selected.'
    if (!sensor.hasAccess) return 'You do not have access to modify sensors on this plant.'
    if (!isSensorStatusEditable(sensor.status))
        return `Sensors with status "${sensor.status ?? 'unknown'}" cannot be modified.`
    return ''
}
